import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { Building2 } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toApiError } from '@/api/errors'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { settingsQuery, useCreateSchool } from '../api'
import { SuffixInput } from './SuffixInput'
import { createSchoolSchema, type CreateSchoolForm } from '../schemas'
import type { Invitation, PlatformSchool } from '../types'
import { InvitationLink } from './InvitationLink'
import { ModulePicker } from './ModulePicker'
import { SCHOOL_MODULES, type SchoolModuleKey } from '@/features/school/modules'
import { SCHOOL_TYPES } from '@/features/school/vocabulary'
import { tAny } from '@/i18n'

const FIELDS = [
  'name',
  'shortName',
  'email',
  'phone',
  'countryCode',
  'currency',
  'timezone',
  'locale',
  'type',
  'adminName',
  'adminEmail',
  'subdomain',
] as const

/** "Lycée Saint-Michel" → "lycee-saint-michel" (a starting suggestion for the address). */
function slugify(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '')
}

function timezones(): string[] {
  try {
    return Intl.supportedValuesOf('timeZone')
  } catch {
    return ['UTC']
  }
}

/** Create a school and invite its first administrator; then show the invitation link. */
export function CreateSchoolDialog({ onOpenChange }: { onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation('platform')
  const create = useCreateSchool()
  const navigate = useNavigate()
  const [done, setDone] = useState<{ school: PlatformSchool; invitation: Invitation } | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [modules, setModules] = useState<SchoolModuleKey[]>([...SCHOOL_MODULES])
  const site = useQuery(settingsQuery())
  const {
    register,
    handleSubmit,
    setError,
    setValue,
    getFieldState,
    formState: { errors },
  } = useForm<CreateSchoolForm>({
    resolver: zodResolver(createSchoolSchema),
    defaultValues: {
      name: '',
      shortName: '',
      email: '',
      phone: '',
      countryCode: '',
      currency: 'EUR',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      locale: 'fr',
      type: 'combined',
      adminName: '',
      adminEmail: '',
      subdomain: '',
    },
  })

  const onSubmit = handleSubmit(async (v) => {
    setFormError(null)
    try {
      setDone(
        await create.mutateAsync({
          ...v,
          shortName: v.shortName || null,
          email: v.email || null,
          phone: v.phone || null,
          countryCode: v.countryCode || null,
          subdomain: v.subdomain || null,
          modules,
        }),
      )
    } catch (e) {
      if (!applyServerErrors(e, setError, FIELDS)) setFormError(toApiError(e).message)
    }
  })

  if (done) {
    return (
      <Dialog
        open
        onOpenChange={onOpenChange}
        size="md"
        icon={<Building2 />}
        title={t('{{name}} is ready', { name: done.school.name })}
        description={t('Its administrator chooses a password with the link, then sets the school up.')}
        footer={
          <>
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              {t('Close')}
            </Button>
            <Button onClick={() => void navigate(`/platform/schools/${done.school.id}`)}>
              {t('Open the school')}
            </Button>
          </>
        }
      >
        <InvitationLink invitation={done.invitation} email={done.school.admin?.email ?? ''} />
      </Dialog>
    )
  }

  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      size="lg"
      icon={<Building2 />}
      title={t('Create a school')}
      description={t('The school starts empty; its administrator is guided through the set-up.')}
      dismissible={!create.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            {t('Cancel')}
          </Button>
          <Button type="submit" form="create-school" loading={create.isPending}>
            {t('Create and invite')}
          </Button>
        </>
      }
    >
      <form id="create-school" onSubmit={(e) => void onSubmit(e)} noValidate className="space-y-6">
        {formError ? (
          <Alert tone="danger" title={t('The school was not created')}>
            {formError}
          </Alert>
        ) : null}
        <fieldset>
          <legend className="mb-3 text-sm font-semibold text-ink">{t('School')}</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label={t('Name')} error={errors.name?.message} required className="sm:col-span-2">
              <Input
                autoFocus
                {...register('name', {
                  onChange: (e: { target: { value: string } }) => {
                    if (!getFieldState('subdomain').isDirty) setValue('subdomain', slugify(e.target.value))
                  },
                })}
              />
            </FormField>
            <FormField
              label={t('Address')}
              error={errors.subdomain?.message}
              required={!!site.data?.baseDomain}
              className="sm:col-span-2"
              hint={
                site.data?.baseDomain
                  ? t('Staff, students and parents sign in at this address. HTTPS is set up automatically.')
                  : t('Set the base domain in Settings to give schools their own address.')
              }
            >
              <SuffixInput
                suffix={site.data?.baseDomain ? `.${site.data.baseDomain}` : null}
                {...register('subdomain')}
              />
            </FormField>
            <FormField label={t('Short name')} error={errors.shortName?.message}>
              <Input {...register('shortName')} />
            </FormField>
            <FormField label={t('Language')} error={errors.locale?.message} required>
              <Select
                options={[
                  { value: 'fr', label: 'Français' },
                  { value: 'en', label: 'English' },
                ]}
                {...register('locale')}
              />
            </FormField>
            <FormField label={t('Type of institution')} error={errors.type?.message} required>
              <Select
                options={SCHOOL_TYPES.map((s) => ({ value: s.value, label: tAny(s.label) }))}
                {...register('type')}
              />
            </FormField>
            <FormField label={t('School email')} error={errors.email?.message}>
              <Input type="email" {...register('email')} />
            </FormField>
            <FormField label={t('Phone')} error={errors.phone?.message}>
              <Input type="tel" {...register('phone')} />
            </FormField>
            <FormField
              label={t('Country code')}
              error={errors.countryCode?.message}
              hint={t('Two letters, e.g. FR, CI, SN')}
            >
              <Input
                maxLength={2}
                className="uppercase"
                {...register('countryCode', { setValueAs: (v: string) => v.toUpperCase() })}
              />
            </FormField>
            <FormField
              label={t('Currency')}
              error={errors.currency?.message}
              required
              hint={t('e.g. EUR, XOF, USD')}
            >
              <Input
                maxLength={3}
                className="uppercase"
                {...register('currency', { setValueAs: (v: string) => v.toUpperCase() })}
              />
            </FormField>
            <FormField
              label={t('Time zone')}
              error={errors.timezone?.message}
              required
              className="sm:col-span-2"
            >
              <Select options={timezones().map((z) => ({ value: z, label: z }))} {...register('timezone')} />
            </FormField>
          </div>
        </fieldset>
        <fieldset className="border-t border-line pt-5">
          <legend className="sr-only">{t('First administrator')}</legend>
          <p className="text-sm font-semibold text-ink" aria-hidden="true">
            {t('First administrator')}
          </p>
          <p className="mt-0.5 mb-3 text-xs text-muted">
            {t('They receive an email to choose their password and can then invite their team.')}
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label={t('Full name')} error={errors.adminName?.message} required>
              <Input autoComplete="off" {...register('adminName')} />
            </FormField>
            <FormField label={t('Email')} error={errors.adminEmail?.message} required>
              <Input type="email" autoComplete="off" {...register('adminEmail')} />
            </FormField>
          </div>
        </fieldset>
        <fieldset className="border-t border-line pt-5">
          <legend className="sr-only">{t('Modules')}</legend>
          <p className="text-sm font-semibold text-ink" aria-hidden="true">
            {t('Modules')}
          </p>
          <p className="mt-0.5 mb-3 text-xs text-muted">
            {t(
              'People, classes, the dashboard and the portals are always included. You can change this later.',
            )}
          </p>
          <ModulePicker value={modules} onChange={setModules} disabled={create.isPending} />
        </fieldset>
      </form>
    </Dialog>
  )
}
